import { fromZonedTime, toZonedTime } from "date-fns-tz";

import type {
  ExecutiveSummarySchedule,
  ExecutiveSummaryScheduleFrequency,
} from "@mapmycustomers/shared/types/entity/executiveSummarySchedule";

import { DayOfWeek, Frequency } from "@app/component/executiveSummary/enums";
import type { ScheduleConfig } from "@app/component/executiveSummary/types";
import { formatDate } from "@app/util/formatters";

export const DEFAULT_HOUR = 8;
export const DEFAULT_MINUTE = 0;

export const getDefaultTime = (): Date => {
  const date = new Date();
  date.setHours(DEFAULT_HOUR, DEFAULT_MINUTE, 0, 0);
  return date;
};

const TIME_STEP_MINUTES = 10;
const pad2 = (value: number): string => String(value).padStart(2, "0");

// 24h "HH:mm" key the time dropdown filters/stores on (not user-facing).
export const dateToTimeValue = (date: Date | undefined): string =>
  `${pad2(date ? date.getHours() : DEFAULT_HOUR)}:${pad2(date ? date.getMinutes() : DEFAULT_MINUTE)}`;

export const timeValueToDate = (value: string): Date => {
  const [hour, minute] = value.split(":").map(Number);
  const date = new Date();
  date.setHours(hour, minute, 0, 0);
  return date;
};

// Searchable time options at 10-min steps; label is the locale-formatted time.
export const buildTimeOptions = (): Array<{ label: string; value: string }> => {
  const options: Array<{ label: string; value: string }> = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += TIME_STEP_MINUTES) {
    const date = new Date();
    date.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0);
    options.push({ label: formatDate(date, "p"), value: dateToTimeValue(date) });
  }
  return options;
};

// Luxon weekday convention used by BE: Mon=1..Sun=7.
export const DAY_OF_WEEK_TO_INT: Record<DayOfWeek, number> = {
  [DayOfWeek.FRIDAY]: 5,
  [DayOfWeek.MONDAY]: 1,
  [DayOfWeek.SATURDAY]: 6,
  [DayOfWeek.SUNDAY]: 7,
  [DayOfWeek.THURSDAY]: 4,
  [DayOfWeek.TUESDAY]: 2,
  [DayOfWeek.WEDNESDAY]: 3,
};

export const INT_TO_DAY_OF_WEEK: Record<number, DayOfWeek> = Object.fromEntries(
  Object.entries(DAY_OF_WEEK_TO_INT).map(([day, weekdayNumber]) => [
    weekdayNumber,
    day as DayOfWeek,
  ])
);

export const FREQUENCY_TO_BE: Record<Frequency, ExecutiveSummaryScheduleFrequency> = {
  [Frequency.DAILY]: "DAILY",
  [Frequency.WEEKLY]: "WEEKLY",
};

// JS Date.getDay(): 0=Sun..6=Sat → luxon: 1=Mon..7=Sun
const toLuxonWeekday = (date: Date): number => ((date.getDay() + 6) % 7) + 1;

export const detectBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

export const formatTimeOfDay = (config: ScheduleConfig): string => {
  const hour = config.time ? config.time.getHours() : DEFAULT_HOUR;
  const minute = config.time ? config.time.getMinutes() : DEFAULT_MINUTE;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

export const computeNextRunAt = (config: ScheduleConfig, timezone: string): string => {
  const hour = config.time ? config.time.getHours() : DEFAULT_HOUR;
  const minute = config.time ? config.time.getMinutes() : DEFAULT_MINUTE;
  const now = new Date();

  // Read today's wall-clock in `timezone`; later we hand the same fields
  let candidate = toZonedTime(now, timezone);
  candidate.setHours(hour, minute, 0, 0);

  const targetWeekday =
    config.frequency === Frequency.WEEKLY ? DAY_OF_WEEK_TO_INT[config.dayOfWeek] : null;
  const matches = (date: Date): boolean =>
    targetWeekday == null || toLuxonWeekday(date) === targetWeekday;

  let utc = fromZonedTime(candidate, timezone);
  let guard = 0;
  while (!matches(candidate) || utc.getTime() <= now.getTime()) {
    candidate = new Date(candidate);
    candidate.setDate(candidate.getDate() + 1);
    utc = fromZonedTime(candidate, timezone);
    if (++guard > 14) {
      break;
    }
  }
  return utc.toISOString();
};

export const toScheduleConfig = (schedule: ExecutiveSummarySchedule): ScheduleConfig => {
  const [hours, minutes] = schedule.timeOfDay.split(":");
  const time = new Date();
  time.setHours(Number(hours), Number(minutes), 0, 0);
  return {
    dayOfWeek: INT_TO_DAY_OF_WEEK[schedule.dayOfWeek ?? 1] ?? DayOfWeek.MONDAY,
    frequency: schedule.frequency === "WEEKLY" ? Frequency.WEEKLY : Frequency.DAILY,
    time,
  };
};
